import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type DeviceGroup, type SourceDetail, type SourceSummary } from '../lib/api'
import { fmtAgo, fmtNumber } from '../lib/format'
import { useAuth } from '../lib/auth'
import { Card, Check, Empty, Field, GroupChip, Loading, Modal, Pill, SecretReveal } from '../components/ui'

export default function Sources() {
  const qc = useQueryClient()
  const { can } = useAuth()
  const [editing, setEditing] = useState<string | 'new' | null>(null)

  const sources = useQuery({
    queryKey: ['sources'],
    queryFn: () => api.get<SourceSummary[]>('/api/admin/sources'),
  })

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1>Sources</h1>
          <div className="page-sub">
            Applications allowed to submit messages. Each owns its API keys, its device pool and its own
            auto-reply rules.
          </div>
        </div>
        {can('Admin') && (
          <button className="btn primary" onClick={() => setEditing('new')}>
            New source
          </button>
        )}
      </div>

      <Card flush>
        {sources.isLoading ? (
          <Loading />
        ) : sources.data?.length === 0 ? (
          <Empty title="No sources yet" hint="Create one, then generate an API key for it." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Device pool</th>
                  <th>Inbound</th>
                  <th className="num">Keys</th>
                  <th className="num">Sent today</th>
                  <th className="num">Rate limit</th>
                </tr>
              </thead>
              <tbody>
                {sources.data?.map((s) => (
                  <tr key={s.id} className="clickable" onClick={() => setEditing(s.id)}>
                    <td>
                      <strong>{s.name}</strong>
                      <div className="faint small mono">{s.slug}</div>
                    </td>
                    <td>
                      <Pill tone={s.isActive ? 'ok' : 'idle'}>{s.isActive ? 'active' : 'disabled'}</Pill>
                    </td>
                    <td>
                      <div className="row wrap" style={{ gap: 5 }}>
                        {s.groups.length === 0 ? (
                          <span className="faint small">whole fleet</span>
                        ) : (
                          s.groups.map((g) => <GroupChip key={g.id} name={g.name} colour={g.colour} />)
                        )}
                      </div>
                    </td>
                    <td className="small muted">{s.inboundMode}</td>
                    <td className="num">{s.activeKeys}</td>
                    <td className="num">{fmtNumber(s.sentToday)}</td>
                    <td className="num faint small">{s.rateLimitPerMinute} seg/min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <SourceModal
          sourceId={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['sources'] })
          }}
        />
      )}
    </div>
  )
}

const ALL_SCOPES = ['sms:send', 'sms:read', 'sms:inbound', 'sms:trace', 'sms:fleet']
const WEBHOOK_EVENTS = [
  'message.accepted',
  'delivery.submitted',
  'delivery.delivered',
  'delivery.failed',
  'delivery.expired',
  'message.completed',
  'inbound.received',
]

function SourceModal({
  sourceId,
  onClose,
  onSaved,
}: {
  sourceId: string | null
  onClose: () => void
  onSaved: () => void
}) {
  const qc = useQueryClient()
  const { can } = useAuth()
  const [tab, setTab] = useState<'settings' | 'keys' | 'pool'>('settings')
  const [error, setError] = useState<string | null>(null)
  const [newKey, setNewKey] = useState<string | null>(null)
  const [newSecret, setNewSecret] = useState<string | null>(null)

  const detail = useQuery({
    queryKey: ['source', sourceId],
    queryFn: () => api.get<SourceDetail>(`/api/admin/sources/${sourceId}`),
    enabled: sourceId !== null,
  })

  const groups = useQuery({
    queryKey: ['device-groups'],
    queryFn: () => api.get<DeviceGroup[]>('/api/admin/device-groups'),
  })

  const [form, setForm] = useState(() => blank())
  const [loaded, setLoaded] = useState(false)
  const [groupIds, setGroupIds] = useState<string[]>([])

  // Seed the form once the record arrives. Keeping it in state (rather than deriving) is what
  // lets the whole thing be edited before saving.
  if (detail.data && !loaded) {
    setForm({
      name: detail.data.name,
      slug: detail.data.slug,
      isActive: detail.data.isActive,
      webhookUrl: detail.data.webhookUrl ?? '',
      webhookEvents: detail.data.webhookEvents,
      inboundMode: detail.data.inboundMode,
      inboundKeywords: detail.data.inboundKeywords.join(', '),
      rateLimitPerMinute: detail.data.rateLimitPerMinute,
      dailyQuota: detail.data.dailyQuota,
      maxPartsPerMessage: detail.data.maxPartsPerMessage,
      defaultValiditySeconds: detail.data.defaultValiditySeconds,
      retryOnUnknown: detail.data.retryOnUnknown,
      allowClientHint: detail.data.allowClientHint,
      allowInternational: detail.data.allowInternational,
      correlationTtlDays: detail.data.correlationTtlDays,
      honourOptOut: detail.data.honourOptOut,
    })
    setGroupIds(detail.data.groupIds)
    setLoaded(true)
  }

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        inboundKeywords: form.inboundKeywords
          .split(',')
          .map((k) => k.trim().toUpperCase())
          .filter(Boolean),
      }
      const saved = sourceId
        ? await api
            .put<{ ok: boolean; webhookSecret: string | null }>(`/api/admin/sources/${sourceId}`, body)
            .then((r) => ({ id: sourceId, webhookSecret: r.webhookSecret }))
        : await api.post<{ id: string; webhookSecret: string | null }>('/api/admin/sources', body)

      await api.put(`/api/admin/sources/${saved.id}/groups`, { groupIds })
      return saved
    },
    onSuccess: (saved) => {
      onSaved()
      void qc.invalidateQueries({ queryKey: ['source', sourceId] })

      // Setting a webhook URL for the first time mints the signing secret, and this is the only
      // moment it exists outside the database. Closing the dialog on it would lose it for good, so
      // the dialog stays open until it has been read.
      if (saved.webhookSecret) setNewSecret(saved.webhookSecret)
      else onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save.'),
  })

  const createKey = useMutation({
    mutationFn: (body: { name: string; isTestKey: boolean; scopes: string[] }) =>
      api.post<{ apiKey: string }>(`/api/admin/sources/${sourceId}/api-keys`, body),
    onSuccess: (r) => {
      setNewKey(r.apiKey)
      void qc.invalidateQueries({ queryKey: ['source', sourceId] })
      void qc.invalidateQueries({ queryKey: ['sources'] })
    },
  })

  const rotateSecret = useMutation({
    mutationFn: () =>
      api.post<{ webhookSecret: string }>(`/api/admin/sources/${sourceId}/webhook-secret`, {}),
    onSuccess: (r) => {
      setNewSecret(r.webhookSecret)
      void qc.invalidateQueries({ queryKey: ['source', sourceId] })
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not rotate the secret.'),
  })

  const revokeKey = useMutation({
    mutationFn: (id: string) => api.del(`/api/admin/api-keys/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['source', sourceId] })
      void qc.invalidateQueries({ queryKey: ['sources'] })
    },
  })

  const set = <K extends keyof ReturnType<typeof blank>>(k: K, v: ReturnType<typeof blank>[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  return (
    <Modal
      title={sourceId ? form.name || 'Source' : 'New source'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Close
          </button>
          <button className="btn primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <div className="banner danger">{error}</div>}

      {/* A failed load must say so. Rendering the empty shell instead makes a 500 look like
          "this source has no keys", which is a far more misleading thing to show. */}
      {detail.isError && (
        <div className="banner danger">
          Could not load this source: {(detail.error as ApiError)?.message ?? 'unknown error'}
        </div>
      )}

      <div className="tabs">
        <button className={tab === 'settings' ? 'tab active' : 'tab'} onClick={() => setTab('settings')}>
          Settings
        </button>
        <button className={tab === 'pool' ? 'tab active' : 'tab'} onClick={() => setTab('pool')}>
          Device pool
        </button>
        {sourceId && (
          <button className={tab === 'keys' ? 'tab active' : 'tab'} onClick={() => setTab('keys')}>
            API keys
          </button>
        )}
      </div>

      {tab === 'settings' && (
        <>
          <div className="grid cols-2" style={{ gap: 16 }}>
            <Field label="Name">
              <input value={form.name} onChange={(e) => set('name', e.target.value)} />
            </Field>
            <Field label="Slug" hint="Stable identifier. Cannot be changed once keys exist.">
              <input value={form.slug} onChange={(e) => set('slug', e.target.value)} disabled={!!sourceId} />
            </Field>
          </div>

          <Check checked={form.isActive} onChange={(v) => set('isActive', v)} label="Active" hint="Inactive sources are rejected at the API with 403." />

          <div className="grid cols-2" style={{ gap: 16 }}>
            <Field
              label="Rate limit (segments per minute)"
              hint="One segment = one SMS: parts × recipients. A 200-recipient message costs 200."
            >
              <input
                type="number"
                value={form.rateLimitPerMinute}
                onChange={(e) => set('rateLimitPerMinute', Number(e.target.value))}
              />
            </Field>
            <Field label="Daily quota (segments)" hint="Blank means unlimited. Resets 00:00 Mauritius.">
              <input
                type="number"
                value={form.dailyQuota ?? ''}
                onChange={(e) => set('dailyQuota', e.target.value === '' ? null : Number(e.target.value))}
              />
            </Field>
            <Field label="Max parts per message" hint="Rejects anything that would split further.">
              <input
                type="number"
                value={form.maxPartsPerMessage}
                onChange={(e) => set('maxPartsPerMessage', Number(e.target.value))}
              />
            </Field>
            <Field label="Default validity (seconds)">
              <input
                type="number"
                value={form.defaultValiditySeconds}
                onChange={(e) => set('defaultValiditySeconds', Number(e.target.value))}
              />
            </Field>
          </div>

          <Check
            checked={form.retryOnUnknown}
            onChange={(v) => set('retryOnUnknown', v)}
            label="Retry when a device never reports an outcome"
            hint="On means a possible duplicate. Off means a possible silent loss, reported as unknown_outcome."
          />
          <Check
            checked={form.allowClientHint}
            onChange={(v) => set('allowClientHint', v)}
            label="Allow the source to request a specific device"
          />
          <Check
            checked={form.allowInternational}
            onChange={(v) => set('allowInternational', v)}
            label="Allow non-Mauritius numbers"
            hint="International SMS costs differ wildly; off by default so a prepaid SIM cannot be drained silently."
          />
          <Check
            checked={form.honourOptOut}
            onChange={(v) => set('honourOptOut', v)}
            label="Honour STOP from recipients"
            hint="A recipient texting STOP is suppressed for this source only, and START puts them back. Turn off only for traffic the recipient asked for individually, such as one-time passwords."
          />

          <Field label="Correlation pin lifetime (days)" hint="How long a conversation stays pinned to one device.">
            <input
              type="number"
              value={form.correlationTtlDays}
              onChange={(e) => set('correlationTtlDays', Number(e.target.value))}
            />
          </Field>

          <h3 style={{ marginTop: 22, marginBottom: 10 }}>Inbound</h3>
          <div className="grid cols-2" style={{ gap: 16 }}>
            <Field label="Mode">
              <select value={form.inboundMode} onChange={(e) => set('inboundMode', e.target.value)}>
                <option value="None">None</option>
                <option value="Webhook">Webhook</option>
                <option value="Poll">Poll</option>
              </select>
            </Field>
            <Field label="Keywords" hint="Comma separated. Routes inbound whose first word matches.">
              <input
                value={form.inboundKeywords}
                onChange={(e) => set('inboundKeywords', e.target.value)}
                placeholder="STOP, ORDER"
              />
            </Field>
          </div>

          <Field label="Webhook URL" hint="Must be HTTPS and resolve to a public address.">
            <input
              value={form.webhookUrl}
              onChange={(e) => set('webhookUrl', e.target.value)}
              placeholder="https://app.example.com/hooks/sms"
            />
          </Field>

          <div className="field-label">Webhook events</div>
          <div className="row wrap" style={{ gap: 12, marginBottom: 10 }}>
            {WEBHOOK_EVENTS.map((ev) => (
              <label key={ev} className="check">
                <input
                  type="checkbox"
                  checked={form.webhookEvents.includes(ev)}
                  onChange={(e) =>
                    set(
                      'webhookEvents',
                      e.target.checked
                        ? [...form.webhookEvents, ev]
                        : form.webhookEvents.filter((x) => x !== ev),
                    )
                  }
                />
                <span className="mono small">{ev}</span>
              </label>
            ))}
          </div>

          <Field
            label="Signing secret"
            hint="Every webhook carries X-Sms-Signature = v1=hex(HMAC-SHA256(secret, timestamp + '.' + body)). Hex-decode this to 32 raw bytes before using it as the key."
          >
            {detail.data?.hasWebhookSecret ? (
              <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                <Pill tone="ok">set</Pill>
                <span className="small muted">
                  Shown once when it was created. Rotate to get a new one — signatures made with the old
                  secret stop verifying immediately.
                </span>
                {can('Admin') && (
                  <button
                    type="button"
                    className="btn ghost"
                    disabled={rotateSecret.isPending}
                    onClick={() => rotateSecret.mutate()}
                  >
                    Rotate
                  </button>
                )}
              </div>
            ) : (
              <span className="small muted">
                None yet — one is generated and shown to you once, the first time you save a webhook URL.
              </span>
            )}
          </Field>

          {newSecret && (
            <div style={{ marginTop: 10 }}>
              <SecretReveal label="Webhook signing secret" value={newSecret} />
              <div className="small muted" style={{ marginTop: 6 }}>
                Copy it now. It is not stored anywhere you can read it back.
              </div>
              <button type="button" className="btn" style={{ marginTop: 10 }} onClick={onClose}>
                Done
              </button>
            </div>
          )}
        </>
      )}

      {tab === 'pool' && (
        <>
          <div className="banner info">
            <div>
              Pick the groups this source may send through. With none selected it uses every approved device — the
              right default when any SIM will do.
            </div>
          </div>
          {groups.data?.length === 0 ? (
            <Empty title="No device groups exist yet" hint="Create one on the Device groups page." />
          ) : (
            <div className="stack" style={{ gap: 8 }}>
              {groups.data?.map((g) => (
                <label
                  key={g.id}
                  className="row"
                  style={{
                    gap: 11,
                    padding: '11px 13px',
                    border: '1px solid rgb(var(--line))',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    background: groupIds.includes(g.id) ? 'rgb(var(--surface-2))' : undefined,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={groupIds.includes(g.id)}
                    onChange={(e) =>
                      setGroupIds((ids) => (e.target.checked ? [...ids, g.id] : ids.filter((x) => x !== g.id)))
                    }
                  />
                  <i className="dot" style={{ background: g.colour ?? 'rgb(var(--glow))', width: 8, height: 8 }} />
                  <div style={{ flex: 1 }}>
                    <strong>{g.name}</strong>
                    <div className="faint small">
                      {g.members.length} device{g.members.length === 1 ? '' : 's'}
                      {!g.isActive && ' · inactive'}
                      {g.description ? ` · ${g.description}` : ''}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'keys' && sourceId && (
        <>
          {newKey && (
            <div className="banner ok" style={{ display: 'block' }}>
              <SecretReveal label="New API key" value={newKey} />
            </div>
          )}

          <div className="btn-row" style={{ marginBottom: 14 }}>
            <button
              className="btn primary sm"
              onClick={() =>
                createKey.mutate({ name: 'Live key', isTestKey: false, scopes: ['sms:send', 'sms:read'] })
              }
            >
              New live key
            </button>
            <button
              className="btn sm"
              onClick={() => createKey.mutate({ name: 'Test key', isTestKey: true, scopes: ALL_SCOPES })}
            >
              New test key
            </button>
          </div>
          <div className="field-hint" style={{ marginTop: -6, marginBottom: 14 }}>
            Test keys run the whole pipeline but never reach a device, so an integration can be built without
            spending credit.
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Prefix</th>
                  <th>Scopes</th>
                  <th>Last used</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {detail.data?.apiKeys.map((k) => (
                  <tr key={k.id} style={{ opacity: k.revokedAt ? 0.45 : 1 }}>
                    <td>
                      {k.name}
                      {k.isTestKey && <span className="chip" style={{ marginLeft: 7 }}>test</span>}
                    </td>
                    <td className="mono small">{k.keyPrefix}</td>
                    <td className="faint small">{k.scopes.join(', ')}</td>
                    <td className="faint small">{k.lastUsedAt ? fmtAgo(k.lastUsedAt) : 'never'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {k.revokedAt ? (
                        <span className="chip">revoked</span>
                      ) : (
                        <button className="btn danger sm" onClick={() => revokeKey.mutate(k.id)}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  )
}

function blank() {
  return {
    name: '',
    slug: '',
    isActive: true,
    webhookUrl: '',
    webhookEvents: [] as string[],
    inboundMode: 'None',
    inboundKeywords: '',
    rateLimitPerMinute: 120,
    dailyQuota: null as number | null,
    maxPartsPerMessage: 6,
    defaultValiditySeconds: 86400,
    retryOnUnknown: true,
    allowClientHint: false,
    allowInternational: false,
    correlationTtlDays: 30,
    honourOptOut: true,
  }
}
